import React, { useEffect, useMemo, useState } from 'react';
import { DialogV2, DrawerV2, Flex, JoyKitThemeProvider } from '@withjoy/joykit';
import { FiltersPanel } from './components/FiltersPanel/FiltersPanel';
import { ListingsPanel } from './components/ListingsPanel/ListingsPanel';
import { Map } from './components/Map/Map';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useListingFilters } from './hooks/useListingFilters';
import { useListingSort } from './hooks/useListingSort';
import { useListingSortingByViewport } from './hooks/useListingSortingByViewport';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationsConsoleDialogProps, AccommodationsConsoleProps, AccommodationsConsoleState, AccommodationListingDTO } from './AccommodationsConsole.types';
import { useAccommodationsConsoleListings } from './hooks/useAccommodationsConsoleListings';
import { AccommodationsConsoleHeader } from './components/Header';
import { EmptyState } from './components/EmptyState';
import { differenceInCalendarDays } from 'date-fns';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { usePagination } from './hooks/usePagination';
import { ShowMoreButton } from './components/ListingsPanel';
import { MobileSearchComponent } from './components/MobileSearchBar';
import { SingleHotelView } from './components/SingleHotelView/SingleHotelView';
import { BottomSheet } from './components/BottomSheet';
import { useAccommodationsConsoleHealth, wrapWithAccommodationsConsoleProvider } from './AccommodationsConsole.health';
import { UseAccommodationsConsoleSearchParamsStateReturn, useAccommodationsConsoleSearchParamsState } from './hooks/useAccommodationsConsoleSearchParamsState';
import { useMobileSheetDisclosures } from './hooks/useMobileSheetDisclosures';
import { getIsValidLatitudeAndLongitude } from './AccommodationsConsole.utils';
import { useMapTelemetry } from './components/Map/Map.telemetry';
import { Map as MapIcon } from '@withjoy/joykit/icons';

import { useMapControls } from './hooks/useMapControls';
import { MapLoader } from './components/Map/MapLoader';
import { withWindow } from '@shared/utils/withWindow';
import { useAccommodationRedirectUrl } from './hooks/useAccommodationRedirectUrl';
import { MobileTransparentDrawer } from '@shared/components/MobileTransparentDrawer';
import { useTranslation } from '@shared/core';
import { SortOption } from './AccommodationsConsole.types';
import { useClearAccommodationConsoleSearchParamsOnUnmount } from './hooks/useClearAccommodationConsoleSearchParamsOnUnmount';
import { useListingsPanelTelemetry } from './components/ListingsPanel/ListingsPanel.telemetry';

const Root = ({
  state,
  eventName,
  onClose,
  onChangeLocation,
  onHeaderSearchSubmit,
  isInitialLoad,
  isShowingAllCouplesPicks
}: {
  state: AccommodationsConsoleState;
  onChangeLocation: UseAccommodationsConsoleSearchParamsStateReturn['handleOnChangeLocation'];
  onHeaderSearchSubmit: UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit'];
  isInitialLoad: boolean;
  isShowingAllCouplesPicks: boolean;
} & AccommodationsConsoleProps) => {
  const telemetry = useMapTelemetry();
  const { isFilterPanelOpen, onFilterPanelOpen, onFilterPanelClose, isSearchOpen, onSearchOpen, onSearchClose } = useMobileSheetDisclosures();
  const { t } = useTranslation('accommodationsConsole');
  const listingPanelTrans = t('listingsPanel');

  const isMobileOrTablet = useIsMobileOrTablet();
  const { onLoadComplete, onLoadFailure } = useAccommodationsConsoleHealth();
  const [hidePanel, togglePanel] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<AccommodationListingDTO | null>(null);
  const { mapViewPort, setMapViewPort, reCenterMap, recenterMapViaLocation, setReCenterMap, recenterMapViaMarkers } = useMapControls();

  const { cachedListings, listings, isFetchingListings, isFetchingListingPrices, pricingError } = useAccommodationsConsoleListings({
    state,
    variant: isShowingAllCouplesPicks ? 'couple' : 'regional',
    onError: error => {
      onLoadFailure(error, { state, filters });
    },
    onSuccess: () => {
      onLoadComplete();
    },
    isInitialLoad
  });

  const { filters, filteredProperties, updateFilters, supportedFacilities, facilityCounts, starRatingCounts, guestRatingCounts, resetFilters } = useListingFilters(
    listings,
    isFetchingListingPrices
  );

  const sortListingsInViewport = useListingSortingByViewport();

  // Filter hotels as per map visible port
  const listingsInViewport = useMemo(() => {
    return sortListingsInViewport(mapViewPort, filteredProperties);
  }, [mapViewPort, sortListingsInViewport, filteredProperties]);

  // Apply sorting based on selected sort option
  const { sortedListings, sortOption, updateSort } = useListingSort(listingsInViewport, state.circularRegionSearch ? state.circularRegionSearch : undefined);

  const handleSearchFormSubmit = useEventCallback<UseAccommodationsConsoleSearchParamsStateReturn['handleHeaderSearchFormSubmit']>(value => {
    onHeaderSearchSubmit(value);
    recenterMapViaLocation(true);
  });

  const mapMarkers = useMemo(() => {
    if (isShowingAllCouplesPicks) {
      return filteredProperties.map(hotel => ({ lat: hotel.location.lat, lng: hotel.location.lng }));
    }
    return [];
  }, [filteredProperties, isShowingAllCouplesPicks]);

  useEffect(() => {
    if (isShowingAllCouplesPicks) {
      recenterMapViaMarkers();
    } else {
      recenterMapViaLocation();
    }
  }, [isShowingAllCouplesPicks, recenterMapViaMarkers, recenterMapViaLocation]);

  const onChangeMapViewPort = useEventCallback((center: { lat: number; lng: number }, bounds: google.maps.LatLngBounds) => {
    setMapViewPort({ center, bounds });
  });

  const getRedirectUrl = useAccommodationRedirectUrl(state);
  const listingTelemetry = useListingsPanelTelemetry();

  const onClickMarker = useEventCallback((hotel: AccommodationListingDTO) => {
    telemetry.mapMarkerClicked({
      listingType: hotel.type,
      placeId: hotel.joyPlaceId,
      hotelName: hotel.name,
      from: 'fullMap'
    });
    if (isMobileOrTablet) {
      setSelectedHotel(hotel);
    } else if (hidePanel) {
      listingTelemetry.seeAvailabilityClicked({ joyPlaceId: hotel?.joyPlaceId || '', hotelName: hotel?.name || '' });
      withWindow(global => {
        global.window.open(getRedirectUrl(hotel), '_blank');
      });
    } else {
      const hotelElement = document.getElementById(`listing-${hotel.joyPlaceId}`);
      if (hotelElement) {
        hotelElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  });

  const handleOnTogglePanel = useEventCallback(() => {
    togglePanel(isOpen => !isOpen);
  });

  const handleResetFilters = useEventCallback(() => {
    resetFilters();
    updateSort(SortOption.COUPLES_PICKS);
    recenterMapViaLocation();
  });

  const noOfNights = useMemo(() => differenceInCalendarDays(new Date(state.searchDates.checkout), new Date(state.searchDates.checkin)), [state.searchDates]);

  const { paginatedListings, showNextPageOfListings, hasMoreListingsToShow, listingsContainerRef } = usePagination({ listings: sortedListings, filters });

  const filtersPanelContent = (
    <SkeletonGroup isReady={!isFetchingListings} placeholder={<FiltersPanel.Skeleton />}>
      <FiltersPanel
        filters={filters}
        eventName={eventName}
        onFiltersChange={updateFilters}
        supportedFacilities={supportedFacilities}
        facilityCounts={facilityCounts}
        starRatingCounts={starRatingCounts}
        prices={listings.map(listing => listing.pricingInfo.values?.nightlyBaseRate).filter(Boolean) as number[]}
        guestRatingCounts={guestRatingCounts}
        isLoadingPrices={isFetchingListingPrices}
        hidePriceRangeFacet={!!pricingError}
        onClose={onFilterPanelClose}
        onReset={handleResetFilters}
        sortOption={sortOption}
        onSortChange={updateSort}
      />
    </SkeletonGroup>
  );

  const listingsPanelContent = (
    <SkeletonGroup
      isReady={!isFetchingListings}
      overflowY={{ _: 'auto', md: 'scroll' }}
      width={{ _: '100%', md: hidePanel ? 0 : 'fit-content' }}
      paddingBottom={9}
      placeholder={<ListingsPanel.Skeleton isMobileOrTablet={isMobileOrTablet} />}
      rootRef={listingsContainerRef}
    >
      {paginatedListings.length > 0 ? (
        <>
          <ListingsPanel
            state={state}
            sortOption={sortOption}
            onSortChange={updateSort}
            noOfNights={noOfNights}
            venueCoordinates={state.circularRegionSearch}
            filters={filters}
            updateFilters={updateFilters}
            listings={paginatedListings}
            isLoadingPrices={isFetchingListingPrices}
            hidePanel={hidePanel}
          />
          {hasMoreListingsToShow && <ShowMoreButton onClick={showNextPageOfListings} />}
        </>
      ) : (
        <EmptyState onReset={handleResetFilters} showResetButton={!!cachedListings?.length} />
      )}
    </SkeletonGroup>
  );

  return (
    <Container>
      <AccommodationsConsoleHeader handleFormSubmit={handleSearchFormSubmit} state={state} onFilterPanelOpen={onFilterPanelOpen} onSearchOpen={onSearchOpen} onClose={onClose} />
      {/* Main Content */}
      <Content>
        {!isMobileOrTablet && (
          <>
            {filtersPanelContent}
            {listingsPanelContent}
          </>
        )}
        {state.circularRegionSearch && getIsValidLatitudeAndLongitude(state.circularRegionSearch.latitude, state.circularRegionSearch.longitude) ? (
          <Map
            location={{ latlng: { lat: state.circularRegionSearch.latitude, lng: state.circularRegionSearch.longitude } }}
            // Passing an empty array when `isFetchingListings` so that we dont see marker artifacts
            hotels={isFetchingListings ? [] : paginatedListings}
            isFetchingHotels={isFetchingListings}
            onChangeLocation={onChangeLocation}
            onChangeMapViewPort={onChangeMapViewPort}
            onClickMarker={onClickMarker}
            isCouplePick={isShowingAllCouplesPicks}
            onToggleLeftPanel={handleOnTogglePanel}
            setReCenterMap={setReCenterMap}
            reCenterMap={reCenterMap}
            hidePanel={hidePanel}
            mapMarkers={isFetchingListings ? [] : mapMarkers}
          />
        ) : null}
        {isFetchingListings && !state.circularRegionSearch && <MapLoader />}
      </Content>
      {isMobileOrTablet && (
        <>
          <MobileTransparentDrawer scrollTopCta={{ icon: MapIcon, text: listingPanelTrans.map(), closeOnClick: true }}>{listingsPanelContent}</MobileTransparentDrawer>
          <BottomSheet isOpen={isFilterPanelOpen} onClose={onFilterPanelClose}>
            {filtersPanelContent}
          </BottomSheet>
          <BottomSheet isOpen={isSearchOpen} onClose={onSearchClose}>
            <MobileSearchComponent onClose={onSearchClose} state={state} handleFormSubmit={handleSearchFormSubmit} />
          </BottomSheet>
          <SingleHotelView
            isOpen={!!selectedHotel}
            onClose={() => setSelectedHotel(null)}
            listings={paginatedListings}
            selectedHotelId={selectedHotel?.joyPlaceId}
            venueCoordinates={state.circularRegionSearch}
            state={state}
            noOfNights={noOfNights}
          />
        </>
      )}
    </Container>
  );
};

const Container = ({ children }: { children?: React.ReactNode }) => {
  return <>{children}</>;
};

const Content = ({ children }: { children: React.ReactNode }) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  return (
    <Flex overflow="auto" height={isMobileOrTablet ? 'calc(100vh - 120px)' : 'auto'} minHeight={isMobileOrTablet ? '400px' : 'auto'}>
      {children}
    </Flex>
  );
};

// this will likely become a provider
export const AccommodationsConsole = ({ eventHandle, eventName, viewMode = 'standalone', onClose }: AccommodationsConsoleProps) => {
  const { state, loading, handleOnChangeLocation, handleHeaderSearchFormSubmit, isInitialLoad, isShowingAllCouplesPicks } = useAccommodationsConsoleSearchParamsState({
    eventHandle
  });
  const isMobileOrTablet = useIsMobileOrTablet();
  useClearAccommodationConsoleSearchParamsOnUnmount(viewMode);

  return (
    <SkeletonGroup
      isReady={!loading || !!state}
      flexDirection="column"
      width={1480}
      height={'100%'}
      maxWidth="100%"
      overflow="auto"
      borderRadius="inherit"
      display="flex"
      placeholder={
        <Container>
          <AccommodationsConsoleHeader.Skeleton />
          <Content>
            <FiltersPanel.Skeleton />
            <ListingsPanel.Skeleton isMobileOrTablet={isMobileOrTablet} />
          </Content>
        </Container>
      }
    >
      {state && (
        <Root
          state={state}
          eventHandle={eventHandle}
          eventName={eventName}
          onClose={onClose}
          onChangeLocation={handleOnChangeLocation}
          onHeaderSearchSubmit={handleHeaderSearchFormSubmit}
          isInitialLoad={isInitialLoad}
          isShowingAllCouplesPicks={isShowingAllCouplesPicks}
        />
      )}
    </SkeletonGroup>
  );
};

export const AccommodationsConsoleWithHealth = wrapWithAccommodationsConsoleProvider(AccommodationsConsole);

export const AccommodationsConsoleDialog = ({ isOpen, onClose, ...restProps }: AccommodationsConsoleDialogProps) => {
  const isMobileOrTablet = useIsMobileOrTablet();

  if (isMobileOrTablet) {
    return (
      <DrawerV2
        isOpen={isOpen}
        overrides={{ Content: { props: { width: '100%', height: '100%', overflow: 'hidden' } } }}
        onClose={onClose}
        scrollBehavior="inside"
        disableFocusLockRemoveScroll
        anchor="bottom"
      >
        <JoyKitThemeProvider>
          <AccommodationsConsoleWithHealth {...restProps} viewMode="overlay" onClose={onClose} />
        </JoyKitThemeProvider>
      </DrawerV2>
    );
  }
  return (
    <>
      <DialogV2
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
        disableFocusLockRemoveScroll
        overrides={{
          Content: {
            props: {
              width: 'auto',
              height: '100%',
              overflow: 'hidden'
            }
          },
          ContentContainer: {
            props: {
              width: 'auto'
            }
          }
        }}
      >
        <JoyKitThemeProvider>
          <AccommodationsConsoleWithHealth {...restProps} viewMode="overlay" onClose={onClose} />
        </JoyKitThemeProvider>
      </DialogV2>
    </>
  );
};
